import React, { useEffect, useState } from "react";
import MarketingHeader from "@/components/layout/MarketingHeader";
import MarketingFooter from "@/components/layout/MarketingFooter";
import { api } from "@/lib/api";
import { optimizeImage } from "@/lib/img";
import { MapPin, Search, ArrowUpRight } from "lucide-react";

const CITIES = ["Phoenix", "Scottsdale", "Gilbert"];
const CATEGORIES = [
  "Beauty",
  "Wellness",
  "Fitness",
  "Health",
  "Restaurants & Café",
  "Bakery & Catering",
  "Floral",
  "Retail & Boutique",
  "Fashion & Apparel",
  "Home & Design",
  "Photography",
  "Events & Hospitality",
  "Childcare & Family",
  "Pet Services",
  "Education & Coaching",
  "Marketing",
  "Professional Services",
  "Real Estate",
  "Nonprofit",
  "Other",
];

export default function HerGuidePublic() {
  const [city, setCity] = useState("");
  const [category, setCategory] = useState("");
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(true);

  const search = async () => {
    setLoading(true);
    const params = {};
    if (city) params.city = city;
    if (category) params.category = category;
    if (q) params.q = q;
    const attempt = async () => {
      const { data } = await api.get("/businesses", { params });
      return data;
    };
    try {
      const data = await attempt();
      setResults(data);
    } catch {
      try {
        await new Promise((r) => setTimeout(r, 600));
        const data = await attempt();
        setResults(data);
      } catch {
        /* silent — empty directory state will be shown */
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { search(); /* eslint-disable-next-line */ }, []);

  return (
    <div className="bg-white min-h-screen" data-testid="her-guide-page">
      <MarketingHeader />
      <section className="pt-28 pb-6 lg:pt-32 lg:pb-16">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 lg:px-14">
          <div className="overline text-[var(--lh-mauve)] mb-6">The HER Guide</div>
          <h1 className="font-serif-display text-5xl md:text-7xl tracking-tighter leading-[0.9]">
            Support local.<br /><em>Support her.</em>
          </h1>
          <p className="mt-6 max-w-xl text-[var(--lh-text)] font-light">
            Every business in The HER Guide is woman-owned, verified, and showing up on purpose.
          </p>

          <div className="mt-10 grid md:grid-cols-12 gap-5 items-end">
            <div className="md:col-span-3">
              <div className="overline text-[var(--lh-muted)] mb-2">City</div>
              <select value={city} onChange={(e) => setCity(e.target.value)} className="input-line" data-testid="guide-city-select">
                <option value="">All cities</option>
                {CITIES.map((c) => <option key={c}>{c}</option>)}
              </select>
            </div>
            <div className="md:col-span-3">
              <div className="overline text-[var(--lh-muted)] mb-2">Category</div>
              <select value={category} onChange={(e) => setCategory(e.target.value)} className="input-line" data-testid="guide-category-select">
                <option value="">All categories</option>
                {CATEGORIES.map((c) => <option key={c}>{c}</option>)}
              </select>
            </div>
            <div className="md:col-span-4">
              <div className="overline text-[var(--lh-muted)] mb-2">Search</div>
              <input value={q} onChange={(e) => setQ(e.target.value)} className="input-line" placeholder="Try 'pilates' or 'florist'" data-testid="guide-search-input" />
            </div>
            <div className="md:col-span-2">
              <button onClick={search} className="btn-primary w-full justify-center" data-testid="guide-search-button">
                {loading ? "…" : "Find"} <Search size={14} />
              </button>
            </div>
          </div>
        </div>
      </section>

      <section className="pt-4 pb-16 lg:py-20" data-testid="guide-results-section">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 lg:px-14">
          <div className="flex items-center justify-between mb-6 lg:mb-8">
            <div className="overline text-[var(--lh-muted)]" data-testid="guide-results-count">{loading ? "Loading…" : `${results.length} ${results.length === 1 ? "business" : "businesses"}`}</div>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {results.map((b) => (
              <article key={b.business_id} className="editorial-card overflow-hidden" data-testid={`guide-card-${b.business_id}`}>
                <div className="aspect-[4/3] overflow-hidden bg-[var(--lh-surface)]">
                  {b.photo_url && <img src={optimizeImage(b.photo_url, { w: 700, name: `localizeher-${b.name || "business"}` })} alt={b.name} loading="lazy" decoding="async" className="w-full h-full object-cover transition-transform duration-700 hover:scale-105" />}
                </div>
                <div className="p-6">
                  <div className="overline text-[var(--lh-mauve)] mb-2">{b.category}</div>
                  <h3 className="font-serif-display text-2xl">{b.name}</h3>
                  <div className="text-xs text-[var(--lh-muted)] flex items-center gap-1.5 mt-1"><MapPin size={12} /> {b.city}</div>
                  <p className="text-sm text-[var(--lh-text)] font-light mt-3 line-clamp-3">{b.description}</p>
                  <div className="flex flex-wrap gap-2 mt-4">
                    {(b.badges || []).slice(0, 2).map((bd) => (
                      <span key={bd} className="text-[0.65rem] tracking-widest uppercase border border-[var(--lh-border)] px-2 py-0.5">{bd}</span>
                    ))}
                  </div>
                  <a href={b.website || "#"} target="_blank" rel="noreferrer" className="mt-5 inline-flex items-center gap-1 overline text-[var(--lh-peach)] hover:text-[var(--lh-warm-ivory)]">
                    Visit <ArrowUpRight size={12} />
                  </a>
                </div>
              </article>
            ))}
          </div>
          {!loading && results.length === 0 && (
            <div className="text-center py-20 text-[var(--lh-muted)]" data-testid="guide-empty">No businesses match your filters.</div>
          )}
        </div>
      </section>
      <MarketingFooter />
    </div>
  );
}
